import type {
  SessionRecapEvent,
  SessionRecapResponse,
} from "../../../shared/session-recap";
import type { SessionRecapAction, SessionRecapLane } from "./session-recap-state";
import {
  SessionRecapStreamError,
  parseSessionRecapResponse,
  readSessionRecapStream,
} from "./session-recap-stream";

export type SessionRecapRequest = (
  path: string,
  init: RequestInit
) => Promise<Response>;

export type SessionRecapTarget = {
  appointmentId: string;
  lane: SessionRecapLane;
  /** Present only on the read-only lane, where it scopes every request to the owning RD. */
  rdId?: string;
  enabled: boolean;
};

export type SessionRecapController = {
  start: () => Promise<void>;
  retry?: () => Promise<void>;
  abort: () => void;
};

export function sessionRecapPath(
  target: Pick<SessionRecapTarget, "appointmentId" | "rdId">,
  suffix: "" | "/stream" | "/retry" = ""
): string {
  const path = `/api/appointments/${encodeURIComponent(target.appointmentId)}/recap${suffix}`;

  return target.rdId ? `${path}?rdId=${encodeURIComponent(target.rdId)}` : path;
}

async function jsonOrNull(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function actionForEvent(event: SessionRecapEvent): SessionRecapAction | null {
  switch (event.type) {
    case "delta":
      return { type: "delta", text: event.text };
    case "completed":
      return {
        type: "completed",
        markdown: event.recap.recap.markdown,
        generatedAt: event.recap.generatedAt,
      };
    case "failed":
      // A stream that finds nothing to generate raced a state change; re-read instead.
      if (event.failure.code === "recap_unavailable")
        throw new SessionRecapStreamError();

      return { type: "failed", retryAllowed: event.failure.retryAllowed };
    default:
      return null;
  }
}

type RecapChannel = {
  target: SessionRecapTarget;
  request: SessionRecapRequest;
  signal: AbortSignal;
  emit: (action: SessionRecapAction) => void;
};

function requestInit(signal: AbortSignal, method: "GET" | "POST" = "GET"): RequestInit {
  return { method, cache: "no-store", signal };
}

async function readDurable(
  channel: RecapChannel
): Promise<SessionRecapResponse | null> {
  try {
    const response = await channel.request(
      sessionRecapPath(channel.target),
      requestInit(channel.signal)
    );
    if (!response.ok) return null;

    return parseSessionRecapResponse(await jsonOrNull(response));
  } catch {
    return null;
  }
}

async function streamEvents(channel: RecapChannel): Promise<void> {
  const response = await channel.request(
    sessionRecapPath(channel.target, "/stream"),
    requestInit(channel.signal)
  );
  if (!response.ok) throw new SessionRecapStreamError();

  for await (const event of readSessionRecapStream(response.body)) {
    const action = actionForEvent(event);
    if (action) channel.emit(action);
  }
}

/**
 * One Appointment's Recap lifecycle over an injected request function: one
 * durable read, at most one stream per generating read, and a Retry POST on
 * the RD lane. Nothing is cached or persisted; every action goes to `dispatch`
 * until `abort`.
 */
export function createSessionRecapController(
  target: SessionRecapTarget,
  request: SessionRecapRequest,
  dispatch: (action: SessionRecapAction) => void
): SessionRecapController {
  const aborter = new AbortController();
  const channel: RecapChannel = {
    target,
    request,
    signal: aborter.signal,
    emit: (action) => {
      if (!aborter.signal.aborted) dispatch(action);
    },
  };
  let rereadAvailable = true;
  let retrying = false;

  async function stream(): Promise<void> {
    try {
      await streamEvents(channel);
    } catch {
      if (aborter.signal.aborted || !rereadAvailable) return;

      rereadAvailable = false;
      await load();
    }
  }

  async function load(): Promise<void> {
    const recap = await readDurable(channel);
    channel.emit({ type: "read", response: recap });
    if (recap?.state === "generating") await stream();
  }

  async function start(): Promise<void> {
    channel.emit({ type: "reset" });
    if (!target.enabled) return;

    rereadAvailable = true;
    await load();
  }

  async function retry(): Promise<void> {
    if (retrying || aborter.signal.aborted) return;

    retrying = true;
    rereadAvailable = true;

    try {
      const response = await request(
        sessionRecapPath(target, "/retry"),
        requestInit(aborter.signal, "POST")
      );
      if (response.status !== 202) return await load();

      channel.emit({ type: "retry_accepted" });
      await stream();
    } catch {
      if (!aborter.signal.aborted) await load();
    } finally {
      retrying = false;
    }
  }

  return {
    start,
    abort: () => aborter.abort(),
    ...(target.lane === "rd" && target.enabled ? { retry } : {}),
  };
}
